import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { useAuth } from '../hooks';
import { getCurrentUser, updateUserProfile as apiUpdateProfile, uploadProfileImage as apiUploadImage } from '../services/api/userApi';
import { logger } from '../utils/logger';
import { toast } from 'react-hot-toast';

// State type
interface UserState {
  extendedProfile: Partial<User> | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: UserState = {
  extendedProfile: null,
  isLoading: false,
  error: null,
};

// Action types
type UserAction =
  | { type: 'FETCH_PROFILE_START' }
  | { type: 'FETCH_PROFILE_SUCCESS'; payload: Partial<User> }
  | { type: 'FETCH_PROFILE_FAILURE'; payload: string }
  | { type: 'UPDATE_PROFILE_START' }
  | { type: 'UPDATE_PROFILE_SUCCESS'; payload: Partial<User> }
  | { type: 'UPDATE_PROFILE_FAILURE'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'FETCH_PROFILE_START':
    case 'UPDATE_PROFILE_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'FETCH_PROFILE_SUCCESS':
      return {
        ...state,
        extendedProfile: action.payload,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_PROFILE_SUCCESS':
      return {
        ...state,
        extendedProfile: {
          ...state.extendedProfile,
          ...action.payload,
        },
        isLoading: false,
        error: null,
      };
    case 'FETCH_PROFILE_FAILURE':
    case 'UPDATE_PROFILE_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context interface
interface UserContextType extends UserState {
  fetchExtendedProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  uploadProfileImage: (file: File) => Promise<string>;
  clearError: () => void;
}

// Create context
export const UserContext = createContext<UserContextType>({
  ...initialState,
  fetchExtendedProfile: async () => {},
  updateUserProfile: async () => {},
  uploadProfileImage: async () => '',
  clearError: () => {},
});

// Provider component
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const { user, isAuthenticated } = useAuth();

  // Initialize with auth user data and then fetch extended profile
  useEffect(() => {
    if (isAuthenticated && user) {
      // Immediately use the auth user data as initial state
      dispatch({ 
        type: 'FETCH_PROFILE_SUCCESS', 
        payload: user 
      });
      
      // Then fetch the complete extended profile
      fetchExtendedProfile();
    }
  }, [isAuthenticated, user]);

  // Fetch extended profile from server
  const fetchExtendedProfile = async (): Promise<void> => {
    try {
      dispatch({ type: 'FETCH_PROFILE_START' });
      
      // Call the API to get user profile
      const userProfile = await getCurrentUser();
      
      // Log the response for debugging
      console.log('Fetched user profile:', userProfile);
      console.log('Meeting passes in profile:', userProfile.meetingPasses);
      
      dispatch({ type: 'FETCH_PROFILE_SUCCESS', payload: userProfile });
    } catch (error: any) {
      logger.error('Error fetching extended profile:', error);
      console.error('Profile fetch error details:', error);
      
      // No need for fallback profile as we already initialized with auth user data
      dispatch({ type: 'FETCH_PROFILE_FAILURE', payload: error.message });
      toast.error('Failed to load profile data');
    }
  };

  // Update user profile with real API
  const updateUserProfile = async (data: Partial<User>): Promise<void> => {
    try {
      dispatch({ type: 'UPDATE_PROFILE_START' });
      
      console.log('Updating profile with data:', data);
      
      // Call the API to update user profile
      const updatedProfile = await apiUpdateProfile(data);
      
      console.log('Profile update response:', updatedProfile);
      
      // Check if we got a valid response
      if (!updatedProfile) {
        throw new Error('Invalid response from server');
      }
      
      dispatch({ type: 'UPDATE_PROFILE_SUCCESS', payload: updatedProfile });
      toast.success('Profile updated successfully');
      
    } catch (error: any) {
      logger.error('Error updating user profile:', error);
      console.error('Profile update error details:', error);
      
      // Optimistic UI update in case of API failure
      // We should ONLY do this during development
      if (import.meta.env.MODE === 'development') {
        console.log('Development mode: applying optimistic update despite error');
        dispatch({ type: 'UPDATE_PROFILE_SUCCESS', payload: data });
        toast.success('Profile updated (dev mode)');
      } else {
        dispatch({ type: 'UPDATE_PROFILE_FAILURE', payload: error.message });
        toast.error('Failed to update profile');
      }
      
      throw error;
    }
  };

  // Upload profile image with real API
  const uploadProfileImage = async (file: File): Promise<string> => {
    try {
      dispatch({ type: 'UPDATE_PROFILE_START' });
      
      console.log('Uploading profile image:', file.name);
      
      // Call the API to upload profile image
      const imageUrl = await apiUploadImage(file);
      
      console.log('Image upload response:', imageUrl);
      
      if (!imageUrl) {
        throw new Error('Invalid response from server');
      }
      
      dispatch({ 
        type: 'UPDATE_PROFILE_SUCCESS', 
        payload: { profilePicture: imageUrl } 
      });
      
      toast.success('Profile image uploaded successfully');
      return imageUrl;
    } catch (error: any) {
      logger.error('Error uploading profile image:', error);
      console.error('Image upload error details:', error);
      
      // Fallback to local URL for development
      const fallbackUrl = URL.createObjectURL(file);
      
      if (import.meta.env.MODE === 'development') {
        console.log('Development mode: using local image URL');
        dispatch({ 
          type: 'UPDATE_PROFILE_SUCCESS', 
          payload: { profilePicture: fallbackUrl } 
        });
        toast.success('Image uploaded (dev mode)');
      } else {
        dispatch({ type: 'UPDATE_PROFILE_FAILURE', payload: error.message });
        toast.error('Failed to upload image');
      }
      
      return fallbackUrl;
    }
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return (
    <UserContext.Provider
      value={{
        ...state,
        fetchExtendedProfile,
        updateUserProfile,
        uploadProfileImage,
        clearError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
